import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Video, VideoOff, Mic, MicOff, Phone, PhoneOff, Monitor, X,
  Loader2, Copy, CheckCircle
} from 'lucide-react';

/**
 * VideoRoom — PeerJS-based WebRTC video call component
 * Usage: <VideoRoom visitCode="abc123" role="staff|family" onEnd={() => {}} />
 */
export default function VideoRoom({ visitCode, patientName, role = 'staff', onEnd }) {
  const [status, setStatus] = useState('connecting'); // connecting, waiting, connected, ended
  const [peerId, setPeerId] = useState('');
  const [remotePeerId, setRemotePeerId] = useState('');
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [callDuration, setCallDuration] = useState(0);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);
  const callRef = useRef(null);
  const streamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const timerRef = useRef(null);

  const myPeerId = `goodwin-${visitCode}-${role}`;
  const remotePeerTarget = `goodwin-${visitCode}-${role === 'staff' ? 'family' : 'staff'}`;

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (callRef.current) callRef.current.close();
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (screenStreamRef.current) screenStreamRef.current.getTracks().forEach(t => t.stop());
    if (peerRef.current) peerRef.current.destroy();
  }, []);

  useEffect(() => {
    let mounted = true;

    const initPeer = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        const { default: Peer } = await import('peerjs');
        const peer = new Peer(myPeerId, {
          debug: 0,
          config: {
            iceServers: [
              { urls: 'stun:stun.l.google.com:19302' },
              { urls: 'stun:stun1.l.google.com:19302' },
            ]
          }
        });

        peerRef.current = peer;

        peer.on('open', (id) => {
          if (!mounted) return;
          setPeerId(id);
          setStatus('waiting');

          // Auto-call if staff
          if (role === 'staff') {
            setTimeout(() => {
              if (!mounted || callRef.current) return;
              tryCall(peer, stream);
            }, 2000);
          }
        });

        peer.on('call', (incomingCall) => {
          if (!mounted) return;
          incomingCall.answer(stream);
          callRef.current = incomingCall;
          handleCall(incomingCall);
        });

        peer.on('error', (err) => {
          if (!mounted) return;
          if (err.type === 'peer-unavailable') {
            setStatus('waiting');
          } else {
            setError(`Connection error: ${err.type}`);
          }
        });

      } catch (err) {
        if (!mounted) return;
        setError('Camera/microphone access denied. Please allow access and try again.');
      }
    };

    const tryCall = (peer, stream) => {
      const outgoing = peer.call(remotePeerTarget, stream);
      if (outgoing) {
        callRef.current = outgoing;
        handleCall(outgoing);
      }
    };

    const handleCall = (call) => {
      call.on('stream', (remoteStream) => {
        if (!mounted) return;
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
        }
        setStatus('connected');
        setRemotePeerId(call.peer);
        timerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
      });

      call.on('close', () => {
        if (!mounted) return;
        setStatus('ended');
        if (timerRef.current) clearInterval(timerRef.current);
      });
    };

    initPeer();

    // Retry connection every 5s if waiting
    const retryInterval = setInterval(() => {
      if (!mounted || status === 'connected' || status === 'ended') return;
      if (peerRef.current && streamRef.current && !callRef.current) {
        const outgoing = peerRef.current.call(remotePeerTarget, streamRef.current);
        if (outgoing) {
          callRef.current = outgoing;
          handleCall(outgoing);
        }
      }
    }, 5000);

    return () => {
      mounted = false;
      clearInterval(retryInterval);
      cleanup();
    };
  }, [visitCode, role]);

  const toggleVideo = () => {
    if (streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (track) { track.enabled = !track.enabled; setVideoEnabled(track.enabled); }
    }
  };

  const toggleAudio = () => {
    if (streamRef.current) {
      const track = streamRef.current.getAudioTracks()[0];
      if (track) { track.enabled = !track.enabled; setAudioEnabled(track.enabled); }
    }
  };

  const toggleScreenShare = async () => {
    if (screenSharing) {
      // Stop screen share — revert to camera
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }
      const cameraTrack = streamRef.current?.getVideoTracks()[0];
      if (cameraTrack && callRef.current) {
        const sender = callRef.current.peerConnection?.getSenders()?.find(s => s.track?.kind === 'video');
        if (sender) await sender.replaceTrack(cameraTrack);
      }
      if (localVideoRef.current && streamRef.current) {
        localVideoRef.current.srcObject = streamRef.current;
      }
      setScreenSharing(false);
    } else {
      // Start screen share
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
        screenStreamRef.current = screenStream;
        const screenTrack = screenStream.getVideoTracks()[0];

        // Replace video track in the active call
        if (callRef.current) {
          const sender = callRef.current.peerConnection?.getSenders()?.find(s => s.track?.kind === 'video');
          if (sender) await sender.replaceTrack(screenTrack);
        }

        // Show screen share in local preview
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }

        setScreenSharing(true);

        // When user stops sharing via browser UI
        screenTrack.onended = () => {
          toggleScreenShare();
        };
      } catch {
        // User cancelled screen share picker
      }
    }
  };

  const endCall = () => {
    cleanup();
    setStatus('ended');
    if (onEnd) onEnd();
  };

  const copyLink = () => {
    const link = `${window.location.origin}/video-visit/${visitCode}?role=family`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="bg-terra-50 rounded-2xl p-6 border border-terra-200 text-center" data-testid="video-room-error">
        <VideoOff className="w-8 h-8 mx-auto mb-2 text-terra-400" />
        <p className="text-sm text-terra-500 mb-3">{error}</p>
        <Button onClick={onEnd} variant="outline" className="rounded-full">Close</Button>
      </div>
    );
  }

  return (
    <div className="bg-[#1a1a1a] rounded-2xl overflow-hidden" data-testid="video-room">
      {/* Video Grid */}
      <div className="relative aspect-video max-h-[500px]">
        {/* Remote Video (large) */}
        <video ref={remoteVideoRef} autoPlay playsInline
          className="w-full h-full object-cover bg-[#2a2a2a]"
          data-testid="video-remote" />

        {/* Waiting overlay */}
        {status === 'waiting' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#2a2a2a]">
            <Loader2 className="w-8 h-8 animate-spin text-sage-400 mb-3" />
            <p className="text-white text-sm font-medium">Waiting for {role === 'staff' ? 'family' : 'clinician'} to join...</p>
            <p className="text-white/50 text-xs mt-1">{patientName && `Visit for ${patientName}`}</p>
            {role === 'staff' && (
              <Button onClick={copyLink} variant="outline" className="mt-4 rounded-full border-white/20 text-white hover:bg-white/10 text-xs"
                data-testid="video-copy-link">
                {copied ? <><CheckCircle className="w-3 h-3 mr-1" />Copied!</> : <><Copy className="w-3 h-3 mr-1" />Copy Family Link</>}
              </Button>
            )}
          </div>
        )}

        {status === 'connecting' && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#2a2a2a]">
            <Loader2 className="w-8 h-8 animate-spin text-sage-400" />
          </div>
        )}

        {/* Local Video (PiP) */}
        <div className="absolute bottom-4 right-4 w-36 h-28 rounded-xl overflow-hidden border-2 border-white/20 bg-[#2a2a2a]">
          <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" data-testid="video-local" />
          {!videoEnabled && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#2a2a2a]">
              <VideoOff className="w-5 h-5 text-white/50" />
            </div>
          )}
        </div>

        {/* Status bar */}
        {status === 'connected' && (
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-white font-medium">{formatTime(callDuration)}</span>
            </div>
            {patientName && (
              <div className="bg-black/40 backdrop-blur-sm rounded-full px-3 py-1">
                <span className="text-xs text-white/80">{patientName}</span>
              </div>
            )}
            {screenSharing && (
              <div className="flex items-center gap-1.5 bg-sky-500/80 backdrop-blur-sm rounded-full px-3 py-1" data-testid="screen-share-indicator">
                <Monitor className="w-3 h-3 text-white" />
                <span className="text-xs text-white font-medium">Sharing Screen</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3 p-4 bg-[#1a1a1a]">
        <button onClick={toggleAudio}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${audioEnabled ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-terra-400 text-white'}`}
          data-testid="video-toggle-audio">
          {audioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button onClick={toggleVideo}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${videoEnabled ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-terra-400 text-white'}`}
          data-testid="video-toggle-video">
          {videoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>
        <button onClick={endCall}
          className="w-14 h-14 rounded-full bg-terra-400 hover:bg-terra-500 flex items-center justify-center text-white transition-colors"
          data-testid="video-end-call">
          <PhoneOff className="w-5 h-5" />
        </button>
        {status === 'connected' && (
          <button onClick={toggleScreenShare}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${screenSharing ? 'bg-sky-500 text-white' : 'bg-white/10 hover:bg-white/20 text-white'}`}
            data-testid="video-toggle-screen">
            <Monitor className="w-5 h-5" />
          </button>
        )}
        {role === 'staff' && (
          <button onClick={copyLink}
            className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            data-testid="video-share-link">
            {copied ? <CheckCircle className="w-5 h-5 text-sage-400" /> : <Copy className="w-5 h-5" />}
          </button>
        )}
      </div>
    </div>
  );
}
